import React, { useContext, useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { Check, Copy, Loader2, Trash2 } from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkPaperSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "./darkTable";

type Row = { code: string; created_at: string };

const Fresh = () => {
  const [data, setData] = useState<Row[]>([]);
  const { reload, setReload } = useContext(RegistrationContext);
  const [reloadingRow, setReloadingRow] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/get-codes/fresh`)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching codes:", error));
  }, []);

  const handleMarkAsSent = async (row: Row) => {
    try {
      setReloadingRow(row.code);
      await axios.post(`${base_url}/api/admin/code-action`, {
        id: row.code,
        action: "sent",
      });
      setData((prev) => prev.filter((item) => item.code !== row.code));
    } catch (error) {
      console.error("Error marking as sent:", error);
    } finally {
      setReloadingRow("");
    }
  };

  const handleDelete = async (row: Row) => {
    try {
      setReloadingRow(row.code);
      await axios.post(`${base_url}/api/admin/code-action`, {
        id: row.code,
        action: "delete",
      });
      setData((prev) => prev.filter((item) => item.code !== row.code));
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setReloadingRow("");
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1200);
  };

  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      Cell: ({ row }: { row: { original: Row } }) => {
        if (reloadingRow === row.original.code) {
          return (
            <span className="inline-flex items-center gap-2 text-slate-400 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…
            </span>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
              {row.original.code}
            </span>
            <button
              onClick={() => handleCopy(row.original.code)}
              className="inline-flex items-center justify-center h-6 w-6 rounded text-slate-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
              title="Copy"
            >
              {copied === row.original.code ? (
                <Check className="h-3.5 w-3.5 text-emerald-300" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        );
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      Cell: ({ row }: { row: { original: Row } }) => {
        const date = new Date(row.original.created_at);
        return <span className="text-slate-300 text-xs">{date.toLocaleDateString()}</span>;
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row }: { row: { original: Row } }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={() => handleMarkAsSent(row.original)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/15 text-blue-200 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            Mark as sent
          </button>
          <button
            onClick={() => handleDelete(row.original)}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 hover:text-rose-200 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-2xl overflow-hidden">
      <MaterialReactTable
        columns={columns}
        data={data}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
        muiTablePaperProps={{ sx: darkPaperSx }}
        muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
        muiTopToolbarProps={{ sx: darkToolbarSx }}
        muiBottomToolbarProps={{ sx: darkToolbarSx }}
        muiTableHeadCellProps={{ sx: darkHeadCellSx }}
        muiTableBodyCellProps={{ sx: darkBodyCellSx }}
        muiTableBodyRowProps={{ sx: darkRowSx }}
        muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
      />
    </div>
  );
};

export default Fresh;
